
import React, { FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { StringIfPlural, useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import { MdOutlineAdd } from 'react-icons/md';


import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, isSearchBoxShowAtom, isSaveLoadingAtom, currentUserSessionAtom } from 'library/store';
import useBusDestinationService, { useBasicFilterBusDestination } from './services/BusDestination';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem, Tabs, Tab, Typography, Chip, FormHelperText, Link } from '@mui/material';
import NumberFormat from 'react-number-format';

import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

import { IBusDestination, defaultBusDestination } from './models/BusDestination';


import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate } from 'features/configuration/services/Enumeration';


import IEnumeration, { Enum_BUS_DESTINATION_OPTION, IEnumerationItem } from 'features/configuration/models/Enumeration';
import { ITarification } from 'features/configuration/models/Tarification';


import { IExtensionType } from 'features/configuration/models/ExtensionType';
import useExtensionTypeService, { useBasicFilterExtensionType } from 'features/configuration/services/ExtensionType';

import { justifyCenter, typographyGroupBoxStyling } from 'themes/commonStyles';

import EntityExpression from 'components/ui/EntityExpression';
import { isFalsy } from 'utility-types';
import EnhancedTable, { ActionIconTableRow } from 'components/ui/EnhancedTable';

export const BusDestinationForm: FC<IBusDestination> = (props: IBusDestination = { ...defaultBusDestination }) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { id } = useParams();

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { language: lg, schoolYears, applicationSetup, roleEntities } = useRecoilValue(currentUserSessionAtom);

  const { retrieveEntity, retrieveData, openEntityActionDrawer,
    checkEntityExpressionSyntax, checkEntitySaveAuthorization } = entityService();
  const { getEnumerationItemsByEnumerationCodes } = useEnumerationService();

  // const { createBusDestination, updateBusDestination,
  //   getSchoolYearTransportFee, createTransportFeeBusDestination } = useBusDestinationService();

  const busDestinationServiceRef = useRef(useBusDestinationService());

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterBusDestination = useBasicFilterBusDestination(
    (event: React.MouseEvent<unknown>, row: IBusDestination) => {
      setIsSearchBoxShow(false);
      _setId(row.id);
    }
  );

  const { canAddEnumerationItem, canUpdateEnumerationItem, displayEnumerationItemDialog,
    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
    onAddItemClick, onUpdateItemClick } = useEnumerationItemCreateUpdate();


  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity(
    (event: React.MouseEvent<unknown>, row: IEntity) => {
      const { name, description } = row;

      // setValue('entityName', name || '');
      // setValue('entityDescription', description || '');

      setOpenEntityFilter(false);
    }
  );

  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration(
    (event: React.MouseEvent<unknown>, row: IEnumeration) => {
      const { id, name, description } = row;

      // setValue('enumerationId', id);
      // setValue('enumerationName', name);

      setOpenEnumerationFilter(false);
    }
  );




  const methods = useForm<IBusDestination>({ defaultValues: { ...defaultBusDestination, schoolYearId: applicationSetup.currentSchoolYearId } });
  const { register, setValue, getValues, watch, reset, handleSubmit, control, formState: { errors } } = methods;

  const watchDestinationOption = watch('destinationOption');
  const watchSchoolYearId = watch('schoolYearId');
  const watchId = watch('id');

  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<IBusDestination>, Error, IBusDestination>(
    _id > 0 ? busDestinationServiceRef.current.updateBusDestination : busDestinationServiceRef.current.createBusDestination, {
    onSuccess: async (data: IResult<IBusDestination>) => {
      enqueueSnackbar(t('_Operation_done'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      _setId(data.data.id);
      //setCurrentEntityIdForAction(data.data.id);

      await refetchBusDestinations();

      queryClient.invalidateQueries(['BusDestination', data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
    }
  });

  const { isLoading: isLoadingTransportFee, isError: isErrorTransportFee, isSuccess: isSuccessTransportFee, error: errorTransportFee,
    mutate: mutateTransportFee } = useMutation<IResult<boolean>, Error, IBusDestination>(
      busDestinationServiceRef.current.createTransportFeeBusDestination, {
      onSuccess: (data: IResult<boolean>) => {
        enqueueSnackbar(t('_Operation_done'), {
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
        });

        //_setId(data.data.id);
        setIsSaveLoading(false);

        setOpenTransportFee(false);
        //queryClient.invalidateQueries(['Registration', data.data.id]);
      },
      onError: (err: Error) => {
        enqueueSnackbar(error?.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
        });
        setOpenTransportFee(false);
        setIsSaveLoading(false);
      }
    });

  const { data: _data, refetch } = useQuery<IBusDestination>(['BusDestination', _id], () => retrieveEntity('BusDestination', _id),
    { refetchOnWindowFocus: false, enabled: false });

  const { data: enumItems, refetch: refetchEnumerationItems } = useQuery<IEnumerationItem[]>(
        ['EnumerationItems', 'BusDestination'], () => getEnumerationItemsByEnumerationCodes([Enum_BUS_DESTINATION_OPTION]));

  const { data: busDestinations, refetch: refetchBusDestinations } = useQuery<IBusDestination[]>(['BusDestinations', 'BusDestination'],
         () => busDestinationServiceRef.current.getBusDestinations({ name: '', description: '', destinationOption: ''}) );

    const busDestinationRowActionIcon = (busDestination: IBusDestination): ActionIconTableRow<IBusDestination, IBusDestination> => {
    
        const res: ActionIconTableRow<IBusDestination, IBusDestination> = {
          toolTip: t('Action'),
          icon: SystemUpdateAltIcon,
          hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
          isActionExecuting: true,
          onRowClickIcon: (event: any, index: number, row: IBusDestination) => {
            openEntityActionDrawer('BusDestination', row.id);
            //(refRemoveBilling.current??emptyFunc)(index);            
          }
        }
        return res;
      }

  const handleBusDestinationDoubleClick = (event: React.MouseEvent<unknown>, row: IBusDestination) => {
      _setId(row.id);
      //reset({...row, schoolYearId: applicationSetup.currentSchoolYearId});
    }


  const refEnumItems = useRef<IEnumerationItem[]>();
  useEffect(() => {
    refEnumItems.current = enumItems;

  }, [enumItems])


  useEffect(() => {
    async function _() {
      const { schoolYearId, id: busDestinationId } = getValues();
      const schoolYearTransportFee = await busDestinationServiceRef.current.getSchoolYearTransportFee(
        schoolYearId, busDestinationId);

      reset({
        ...getValues(),
        defaultArrivalWeeklyFee: schoolYearTransportFee?.defaultArrivalWeeklyFee ?? 0,
        defaultArrivalMonthlyFee: schoolYearTransportFee?.defaultArrivalMonthlyFee ?? 0,
        defaultArrivalQuarterlyFee: schoolYearTransportFee?.defaultArrivalQuarterlyFee ?? 0,
        defaultArrivalQuarterlyLongFee: schoolYearTransportFee?.defaultArrivalQuarterlyLongFee ?? 0,
        defaultArrivalQuarterlyShortFee: schoolYearTransportFee?.defaultArrivalQuarterlyShortFee ?? 0,
        defaultArrivalYearlyFee: schoolYearTransportFee?.defaultArrivalYearlyFee ?? 0,
        defaultDepartureWeeklyFee: schoolYearTransportFee?.defaultDepartureWeeklyFee ?? 0,
        defaultDepartureMonthlyFee: schoolYearTransportFee?.defaultDepartureMonthlyFee ?? 0,
        defaultDepartureQuarterlyFee: schoolYearTransportFee?.defaultDepartureQuarterlyFee ?? 0,
        defaultDepartureQuarterlyLongFee: schoolYearTransportFee?.defaultDepartureQuarterlyLongFee ?? 0,
        defaultDepartureQuarterlyShortFee: schoolYearTransportFee?.defaultDepartureQuarterlyShortFee ?? 0,
        defaultDepartureYearlyFee: schoolYearTransportFee?.defaultDepartureYearlyFee ?? 0,
        defaultRoundTripWeeklyFee: schoolYearTransportFee?.defaultRoundTripWeeklyFee ?? 0,
        defaultRoundTripMonthlyFee: schoolYearTransportFee?.defaultRoundTripMonthlyFee ?? 0,
        defaultRoundTripQuarterlyFee: schoolYearTransportFee?.defaultRoundTripQuarterlyFee ?? 0,
        defaultRoundTripQuarterlyLongFee: schoolYearTransportFee?.defaultRoundTripQuarterlyLongFee ?? 0,
        defaultRoundTripQuarterlyShortFee: schoolYearTransportFee?.defaultRoundTripQuarterlyShortFee ?? 0,
        defaultRoundTripYearlyFee: schoolYearTransportFee?.defaultRoundTripYearlyFee ?? 0
      });
    }

    _();
  }, [watchSchoolYearId, watchId]);

  useEffect(() => {
    setCurrentFormNameAtom(t('Bus destination'));
    setCurrentBasicTextFilterProps(basicFilterBusDestination);
  }, []);

  /********** This use effect call retreive data wich will call refetch and _data will be updated. 
    and the new useEffect will take place ********************/
  useEffect(() => {
    // setCurrentFormName(t('Billing'));        

    if (_id > 0)
      retrieveData('BusDestination', _id, refetch);
  }, [_id]);


  useEffect(() => {

    if (_data && _data.id > 0) {
      reset({ ..._data, schoolYearId: applicationSetup.currentSchoolYearId });
    }
  }, [_data]);

  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _setId(0);
    reset({ ...defaultBusDestination, schoolYearId: applicationSetup.currentSchoolYearId });
  }

  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {

    if (!checkEntitySaveAuthorization('BusDestination', _id)) {
      setIsSaveLoading(false);
      return;
    }

    const data = getValues();
    if (data.name.trim() === '' || data.description.trim() === '') {
      enqueueSnackbar(t('Reference is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    mutate(data);
  }

  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityActionDrawer('BusDestination', _id);
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    //    queryClient.invalidateQueries(['RequestType',currentEntityIdForAction]);        
    //    await retreiveData(currentEntityNameForAction,currentEntityIdForAction, refetch);        
    //    reset(_data);        
  }

  const [openTransportFee, setOpenTransportFee] = useState<boolean>(false);
  const actionDataCreateTransportFeeBusDestination = async (event: MouseEvent<HTMLButtonElement>) => {

    setOpenTransportFee(true);
  }

  const handleOkCreateTransportFeeBusDestination = async () => {
    // const { id, newTransportBillings } = getValues();
    // if (newTransportBillings.length === 0) {
    //   enqueueSnackbar(t('No billing to add'), {
    //     variant: 'warning',
    //     anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
    //   });
    //   return;
    // }

    // setOkButtonDisabledNewTransport(true);
    mutateTransportFee({ ...getValues() });
  }

  return (
    <FormProvider {...methods} >
      <Box sx={{ mx: 0.1 }}>
        <Grid container rowSpacing={0.5} columnSpacing={0.1}>
          <Grid item xs={12} md={6} lg={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'  >
              <Box sx={{ mt: 1, width: '100%' }} >
                <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />
                <Button id='btnSave' onClick={saveData} sx={{ display: 'none' }} />
                <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
                <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />

                <Button id='btnActionCreateTransportFeeBusDestination' onClick={actionDataCreateTransportFeeBusDestination} sx={{ display: 'none' }} />

                <TextField sx={{ width: 'calc(15% - 8px)' }} id="id" label={t('Id')} {...register('id')} inputProps={{ readOnly: true }} />

                <TextField sx={{ width: 'calc(65% - 8px)' }} id="name" label={t('Name')} {...register('name')} />
                <FormControlLabel sx={{ width: 'calc(20% - 8px)' }}
                  label={t('Active ?')}
                  control={
                    <Controller
                      name='isActive'
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                    />} />
              </Box>


              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='destinationOption' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(35% - 8px)' }} id="destinationOption"
                      label={t('Option')} inputProps={{ readOnly: false }}
                      helperText={
                        <FormHelperText>
                          {canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_BUS_DESTINATION_OPTION,
                            t('Destination option'), t('Destination option'))}
                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                            {t('Add')}
                          </Link>}
                          {(canUpdateEnumerationItem && !isFalsy(watchDestinationOption)) && <Link href="#" onClick={(event) => {
                            onUpdateItemClick(Enum_BUS_DESTINATION_OPTION, t('Cycle'), t('Cycle'),
                              refEnumItems.current?.find(x => x.enumerationCode === Enum_BUS_DESTINATION_OPTION && x.code === watchDestinationOption)
                            );
                          }}
                            sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                            {t('Update')}
                          </Link>}
                        </FormHelperText>
                      } >
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_BUS_DESTINATION_OPTION).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <TextField sx={{ width: 'calc(65% - 8px)' }} id="description" label={t('Description')} {...register('description')} />
              </Box>

              {openTransportFee && <FormDialog open={openTransportFee} maxWidth='sm'
                okText={t('OK')} cancelText={t('Cancel')} title={`${t('Transport fees')} ...`}

                onCancel={() => { setOpenTransportFee(false); }}
                onClose={() => { setOpenTransportFee(false); }} onOk={handleOkCreateTransportFeeBusDestination}  >
                <Stack flexDirection='column'>
                  <Box sx={{ mt: 1, width: '100%' }}>
                    <Controller name='schoolYearId' control={control}

                      render={({ field: { onChange, value } }) => (
                        <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                          label={t('School year')} inputProps={{ readOnly: false }} focused >
                          {schoolYears && schoolYears.map(
                            (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                          }
                        </TextField>
                      )}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Typography variant="h6" id="tableTitle" color="primary" noWrap
                      sx={{ ...typographyGroupBoxStyling }}>
                      {`${t(('Monthly fees'))}`}
                    </Typography>
                  </Box>
                  <Box sx={{
                    mt: 0.25, width: '100%',
                    display: 'grid', gap: 1.5, // spacing between items (adjust as needed)
                    gridTemplateColumns: {
                      xs: 'repeat(3, 1fr)',  // 3 per row on extra-small screens (sx)
                      sm: 'repeat(3, 1fr)',  // 4 per row on small screens (sm)                                            
                    },
                  }} >
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Arrival')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultArrivalMonthlyFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Departure')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultDepartureMonthlyFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Round trip')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultRoundTripMonthlyFee`}
                      control={control}
                    />
                  </Box>

                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Typography variant="h6" id="tableTitle" color="primary" noWrap
                      sx={{ ...typographyGroupBoxStyling }}>
                      {`${t(('Quarterly fees'))}`}
                    </Typography>
                  </Box>
                  <Box sx={{
                    mt: 0.25, width: '100%',
                    display: 'grid', gap: 1.5, // spacing between items (adjust as needed)
                    gridTemplateColumns: {
                      xs: 'repeat(3, 1fr)',  // 3 per row on extra-small screens (sx)
                      sm: 'repeat(3, 1fr)',  // 4 per row on small screens (sm)                                            
                    },
                  }} >
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Arrival')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultArrivalQuarterlyFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Departure')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultDepartureQuarterlyFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Round trip')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultRoundTripQuarterlyFee`}
                      control={control}
                    />
                  </Box>

                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Typography variant="h6" id="tableTitle" color="primary" noWrap
                      sx={{ ...typographyGroupBoxStyling }}>
                      {`${t(('Quarterly fees'))} - ${t('Long')}`}
                    </Typography>
                  </Box>
                  <Box sx={{
                    mt: 0.25, width: '100%',
                    display: 'grid', gap: 1.5, // spacing between items (adjust as needed)
                    gridTemplateColumns: {
                      xs: 'repeat(3, 1fr)',  // 3 per row on extra-small screens (sx)
                      sm: 'repeat(3, 1fr)',  // 4 per row on small screens (sm)                                            
                    },
                  }} >
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Arrival')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultArrivalQuarterlyLongFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Departure')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultDepartureQuarterlyLongFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Round trip')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultRoundTripQuarterlyLongFee`}
                      control={control}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Typography variant="h6" id="tableTitle" color="primary" noWrap
                      sx={{ ...typographyGroupBoxStyling }}>
                      {`${t(('Quarterly fees'))} - ${t('Short')}`}
                    </Typography>
                  </Box>
                  <Box sx={{
                    mt: 0.25, width: '100%',
                    display: 'grid', gap: 1.5, // spacing between items (adjust as needed)
                    gridTemplateColumns: {
                      xs: 'repeat(3, 1fr)',  // 3 per row on extra-small screens (sx)
                      sm: 'repeat(3, 1fr)',  // 4 per row on small screens (sm)                                            
                    },
                  }} >
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Arrival')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultArrivalQuarterlyShortFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Departure')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultDepartureQuarterlyShortFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Round trip')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultRoundTripQuarterlyShortFee`}
                      control={control}
                    />
                  </Box>
                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Typography variant="h6" id="tableTitle" color="primary" noWrap
                      sx={{ ...typographyGroupBoxStyling }}>
                      {`${t(('Yearly fees'))}`}
                    </Typography>
                  </Box>
                  <Box sx={{
                    mt: 0.25, width: '100%',
                    display: 'grid', gap: 1.5, // spacing between items (adjust as needed)
                    gridTemplateColumns: {
                      xs: 'repeat(3, 1fr)',  // 3 per row on extra-small screens (sx)
                      sm: 'repeat(3, 1fr)',  // 4 per row on small screens (sm)                                            
                    },
                  }} >
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Arrival')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultArrivalYearlyFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Departure')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultDepartureYearlyFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Round trip')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultRoundTripYearlyFee`}
                      control={control}
                    />
                  </Box>

                  <Box sx={{ mt: 1, width: '100%' }} >
                    <Typography variant="h6" id="tableTitle" color="primary" noWrap
                      sx={{ ...typographyGroupBoxStyling }}>
                      {`${t(('Weekly fees'))}`}
                    </Typography>
                  </Box>
                  <Box sx={{
                    mt: 0.25, width: '100%',
                    display: 'grid', gap: 1.5, // spacing between items (adjust as needed)
                    gridTemplateColumns: {
                      xs: 'repeat(3, 1fr)',  // 3 per row on extra-small screens (sx)
                      sm: 'repeat(3, 1fr)',  // 4 per row on small screens (sm)                                            
                    },
                  }} >
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Arrival')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultArrivalWeeklyFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Departure')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultDepartureWeeklyFee`}
                      control={control}
                    />
                    <Controller
                      render={({ field: { onChange, onBlur, name, value, ref } }) => {
                        return (
                          <NumberFormat
                            decimalScale={3}
                            label={`${t('Round trip')}`} sx={{ width: 'calc(100% - 8px)' }}
                            allowEmptyFormatting={false}
                            control={control}

                            onValueChange={(v) => onChange(v.floatValue)}
                            //fixedDecimalScale={true} 
                            thousandSeparator={lg.includes('fr') ? ' ' : ','}
                            //{...field}
                            customInput={TextFieldRight}
                            defaultValue={value}
                            value={value}
                          />
                        );
                      }}
                      name={`defaultRoundTripWeeklyFee`}
                      control={control}
                    />
                  </Box>

                </Stack>
              </FormDialog>}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} lg={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'  >
              <Box sx={{ mt: 1, width: '100%' }} >
                <EnhancedTable<IBusDestination>
                    rows={busDestinations||[]}
                    headCells={[
                      //{id:'id', label : t('Id'),  display: true, type: 'numeric', },
                      { id: 'id', label: t('Id'), display: true, type: 'string' },
  
                      { id: 'name', label: t('Name'), display: true, type: 'string' },
                      { id: 'description', label: t('Description'), display: true, type: 'string' },
                      
                      { id: 'isActive', label: t('Active ?'), display: true, type: 'boolean' },
                    ]}
                    title={t(`List of destinations`)} objKey='id'
                    stateSelected={undefined}
                    onRowSelected={undefined} rowCheckedMode='single'
                    onRowCheckedSelectChange={undefined} order='desc' orderBy='name'
  
                    onRowDoubleClick={handleBusDestinationDoubleClick}
                    //rowActionIcon={busDestinationRowActionIcon}
                    rowActionIcon={undefined}
                    toolbarActions={[
                      // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                    ]}
                  />      
              </Box>
            </Stack>
                        
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  )
}

